import moment from 'moment';
import React, { ReactNode, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';

import { AppointmentStatus } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { Patient, PatientGuideDetails } from '@vestahealthcare/common/models';
import { EMPTY } from '@vestahealthcare/common/utils/constants';
import { validate } from '@vestahealthcare/common/utils/phone';

import {
  Card,
  CardContent,
  CardHeader,
  Fonts,
  IconButton,
} from 'styleguide-v2';

import { showGlobalError } from '../../../../components/GlobalMessage';
import { fetchGuideDetails } from '../../../../redux/slices/MemberInfoSlice';
import { useSelector } from '../../../../redux/store';
import { EditGuideDetails } from './EditModals/EditGuideContactDetail';

interface Props {
  member: Patient;
}

const useStyles = makeStyles({
  cardContainer: {
    '&& h4': {
      alignSelf: 'flex-start',
      textTransform: 'uppercase',
      fontSize: `calc(${Fonts.fontSize} * .625)`,
      fontFamily: Fonts.fontWorkSans,
      lineHeight: '12px',
      margin: '0.25rem 0 0',
    },
    '&& h4 + :not(h4)': {
      fontSize: `calc(${Fonts.fontSize} * .875)`,
      margin: '0',
    },
    '&& p + p': {
      alignSelf: 'flex-start',
      lineHeight: '12px',
      margin: '0.25rem 0 0',
    },
    '&& p': {
      marginBottom: '0.25rem',
    },
    '&& .grid-wrapper .grid-wrapper': {
      gridTemplateRows: 'max-content',
    },
    '&& .grid-wrapper .grid-wrapper:not(:last-child)': {
      borderRight: '2px solid #f4f7f6',
      paddingRight: '7px',
    },
  },
  title: {
    '&&': {
      fontSize: Fonts.fontSize,
      fontFamily: Fonts.fontFamily,
      fontWeight: 500,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
    },
  },
});

const renderFieldDisplay = (
  guideDetails: PatientGuideDetails | undefined,
  fieldKey: keyof PatientGuideDetails,
  label: string,
) => {
  const value = guideDetails?.[fieldKey];
  let displayValue: string | ReactNode = value || EMPTY;
  let hasError = false;
  let errorStyle = {};

  if (fieldKey === 'livesWithMember') {
    displayValue = value
      ? translate('personalDetails.guideDetails.yes')
      : translate('personalDetails.guideDetails.no');
  }

  if (fieldKey === 'initiatingVisit' && value) {
    displayValue = (
      <>
        {moment(value as string).format('MM/DD/YYYY')}
        <br />
        {guideDetails?.visitStatus && (
          <span
            style={{
              fontSize: '0.8em',
              position: 'relative',
              top: '-8px',
              color: AppointmentStatus.getColor(guideDetails?.visitStatus),
            }}
          >
            [{translate(`enums.appointmentStatus.${guideDetails?.visitStatus}`)}
            ]
          </span>
        )}
      </>
    );
  }

  if (fieldKey === 'relationship') {
    displayValue = value
      ? translate(`enums.patientGuideRelationship.${value as string}`)
      : EMPTY;
  }

  if (fieldKey === 'phoneType') {
    displayValue = value
      ? translate(`enums.phoneType.${value as string}`)
      : EMPTY;
  }

  if (
    fieldKey === 'phoneNumber' &&
    ((value && !validate(value as string)) || guideDetails?.invalidPhoneNumber)
  ) {
    hasError = true;
    errorStyle = { color: '#d32f2f' };
  }

  return (
    <>
      <h4 className="grid-span-5">{label}</h4>
      <p className="grid-span-6" style={errorStyle}>
        {displayValue}
        {hasError && (
          <span style={{ fontSize: '0.8em', marginLeft: '8px' }}>
            ({translate('personalDetails.guideDetails.invalidPhone')})
          </span>
        )}
      </p>
    </>
  );
};

const MemberGuideDetails = ({ member }: Props) => {
  const styles = useStyles();
  const dispatch = useDispatch();

  const guideContactInfo = useSelector(
    (state) => state.memberInfoSlice.guideDetails,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const refetchGuideDetails = useCallback(async () => {
    try {
      dispatch(fetchGuideDetails(member.id));
    } catch (error) {
      showGlobalError(error as string);
    }
  }, [dispatch, member.id]);

  return (
    <Card>
      <CardHeader
        title={
          <div className="flex gap">
            <span>{translate('personalDetails.guideDetails.title')}</span>
            <br />
            <IconButton
              disabled={!member?.isEditable()}
              noMargin
              onClick={() => setIsEditModalOpen(true)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </div>
        }
      />
      <CardContent className={styles.cardContainer}>
        <div className="grid-wrapper">
          <div className="grid-span-4 grid-wrapper">
            {renderFieldDisplay(
              guideContactInfo,
              'firstName',
              translate('personalDetails.guideDetails.firstName'),
            )}

            {renderFieldDisplay(
              guideContactInfo,
              'lastName',
              translate('personalDetails.guideDetails.lastName'),
            )}
            {renderFieldDisplay(
              guideContactInfo,
              'phoneNumber',
              translate('personalDetails.guideDetails.phoneNumber'),
            )}

            {renderFieldDisplay(
              guideContactInfo,
              'phoneType',
              translate('personalDetails.guideDetails.phoneType'),
            )}
            {renderFieldDisplay(
              guideContactInfo,
              'relationship',
              translate('personalDetails.guideDetails.relationship'),
            )}

            {renderFieldDisplay(
              guideContactInfo,
              'livesWithMember',
              translate('personalDetails.guideDetails.livesWithMember'),
            )}
          </div>

          <div className="grid-span-4 grid-wrapper">
            {renderFieldDisplay(
              guideContactInfo,
              'initiatingVisit',
              translate('personalDetails.guideDetails.initiatingVisit'),
            )}
          </div>
        </div>

        <EditGuideDetails
          patient={member}
          guideDetails={guideContactInfo}
          onSubmit={refetchGuideDetails}
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        />
      </CardContent>
    </Card>
  );
};

export default MemberGuideDetails;
